import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import HttpManager from "../../../services/HttpManager";
import { telefonoBonito } from "../../../utils/telefonoBonito";
import { FuenteFicha, ProspectCatalogs, ProspectDetails, ProspectEvent } from "../../../services/v2/prospects.service";
import { abrirArchivo } from "../../../components/enrollment/visorArchivo";
import { cuando, errorTexto, ETAPAS, fechaLarga, iniciales as inicialesDe, localMxAIso, nombreCompleto, nombreReal, RESULTADOS_LLAMADA } from "./salesHelpers";
import { CursosCompacto } from "./cursosSelect";
import { banderin, ETAPA_BORDE, limpiarWa, LogoWa, nombreWa, pareceNombre, ST_PILL, territorio, waDiceAlgo } from "./crmLista";
import { cursoCorto } from "../../../utils/cursoCorto";
import CrmBitacora from "./crmBitacora";
import CrmActividades, { Actividades, FormActividad, venceTexto } from "./crmActividades";
import { plantelCorto } from "./crmLista";
import Conversacion from "./conversacion";
import LadoCliente, { EquipoCliente } from "./ladoCliente";
import { AvisoSoloVer, useAcceso } from "./soloVer";
import EquipoChips from "./equipoChips";
import EditarDatos from "./editarDatos";
import PromoModal from "./promoModal";
import NotaModal from "./notaModal";
import { aMarcable, resultadoInicial, resultadoSinRespuesta, telefono } from "./telefono/telefono";
import ReasignarDestino from "./reasignarDestino";
import { ModalCita } from "./formCita";
import { marcador } from "./marcador";

type Sec = "general" | "actividades" | "cronologia";

/** Color de cada status (punto y píldora). Los que sacan de seguimiento van en oro (lo malo en WAPP es oro, no rojo). */
// Los mismos colores que la lista (crmLista.tsx: status relleno, etapa con contorno punteado).
// Verde solo para Matriculado (Esteban, 2026-10-06): ningún status es verde.
const ST_FUERA = ["numero_equivocado", "no_contactar", "spam"];
/** Los mismos colores de la lista (ST_PILL): Nuevo/Recontactó naranja lleno, Promesa azul lleno, Interesado azul claro, Potencial gris, los que sacan de seguimiento en oro. */
const MOTIVOS_OCULTOS = ["heredado de Zoho", "sin status en Zoho"];
const pillSt = (code: string) => ST_PILL[code] || { bg: "#F4ECE6", fg: "#2B1C16" };
const colorSt = (code: string) => { const c = pillSt(code); return c.lleno ? c.bg : c.fg; };

/** Status: uno solo; se cambia con un toque desde el encabezado (Esteban, 2026-10-06). */
/** Potencial pide motivo: los motivos salen dentro del mismo menú y se guardan con un clic (Esteban, 2026-10-10: "potencial no
 *  deja asignarle"; antes el motivo salía aparte, abajo, y el status parecía no cambiar). También sirve para cambiar el motivo. */
const StatusMenu: React.FC<{ statuses: ProspectCatalogs["statuses"]; actual: number; motivo: string | null; motivos: string[]; busy: boolean;
	onElegir: (id: number, motivo: string | null) => void }> = ({ statuses, actual, motivo, motivos, busy, onElegir }) => {
	const [abierto, setAbierto] = useState(false);
	const [conMotivo, setConMotivo] = useState<number | null>(null);
	useEffect(() => { if (!abierto) setConMotivo(null); }, [abierto]);
	const caja = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!abierto) return;
		const cerrar = (e: MouseEvent) => { if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false); };
		const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setAbierto(false); };
		document.addEventListener("mousedown", cerrar);
		document.addEventListener("keydown", esc);
		return () => { document.removeEventListener("mousedown", cerrar); document.removeEventListener("keydown", esc); };
	}, [abierto]);
	const st = statuses.find((x) => x.id === actual);
	const manuales = statuses.filter((x) => x.bool_manual);
	const buenos = manuales.filter((x) => !ST_FUERA.includes(x.code));
	const fuera = manuales.filter((x) => ST_FUERA.includes(x.code));
	const op = (x: ProspectCatalogs["statuses"][number]) => (
		<React.Fragment key={x.id}>
			<button type="button" role="menuitemradio" aria-checked={x.id === actual} aria-expanded={x.bool_needs_reason ? conMotivo === x.id : undefined} className={"fx-op" + (x.id === actual ? " on" : "")}
				onClick={() => { if (x.bool_needs_reason) { setConMotivo(conMotivo === x.id ? null : x.id); return; } setAbierto(false); onElegir(x.id, null); }}>
				<span className="fx-dot" style={{ background: colorSt(x.code) }} />{x.name}
				{x.bool_needs_reason ? <span className="fx-op-mas">{conMotivo === x.id ? "▴" : "▾"}</span> : null}
				{x.id === actual ? <svg className="fx-ok" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg> : null}
			</button>
			{conMotivo === x.id ? (
				<div className="fx-motivos" role="group" aria-label={"¿Por qué " + x.name + "?"}>
					{motivos.map((m) => (
						<button key={m} type="button" className={"fx-op fx-motivo" + (x.id === actual && m === motivo ? " on" : "")} onClick={() => { setAbierto(false); onElegir(x.id, m); }}>{m}</button>
					))}
				</div>
			) : null}
		</React.Fragment>
	);
	const pc = pillSt(st?.code || "");
	return (
		<div className="fx-st" ref={caja}>
			<button type="button" className={"fx-st-btn fz2-pill" + (pc.lleno ? " lleno" : "")} disabled={busy} aria-haspopup="menu" aria-expanded={abierto} onClick={() => setAbierto(!abierto)}
				style={{ color: pc.fg, background: pc.bg, borderColor: pc.lleno ? pc.bg : pc.fg + "55" }}>
				{st ? (st.code === "nuevo_cliente" ? "Nuevo" : st.name) : "Sin status"}{st && motivo ? <span className="fx-st-mot"> · {motivo}</span> : null}
				<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
			</button>
			{abierto ? (
				<div className="fx-pop" role="menu" aria-label="Cambiar status">
					<div className="fx-pop-tit">Cambiar status</div>
					{buenos.map(op)}
					{fuera.length ? <><div className="fx-pop-sep" /><div className="fx-pop-grupo oro">Sacan de seguimiento</div>{fuera.map(op)}</> : null}
				</div>
			) : null}
		</div>
	);
};

/** Etapa: la pone WAPP según su matrícula (no se elige a mano). Misma píldora de contorno que en la lista. */
const PildoraEtapa: React.FC<{ etapa: number | null }> = ({ etapa }) => {
	const c = etapa ? ETAPA_BORDE[etapa] : null;
	return c ? <span className="cl-etapa fz2-etapa" data-etapa={etapa} style={{ borderColor: c, color: c }}>{ETAPAS.find((x) => x.id === etapa)?.name}</span> : <span className="fx-vacio">—</span>;
};

/**
 * Fuente (Esteban, 2026-10-09: "el ad ID y toda esa data de Fuente de Cliente ... esencial para trackear de dónde están
 * saliendo los clientes"): cómo llegó la primera vez y, si volvió por otro anuncio, el último. Se llena sola.
 */
const FuenteCard: React.FC<{ fu: FuenteFicha; dato: (t: string, v: React.ReactNode, ancho?: boolean) => React.ReactNode; onCopiar: (t: string) => void }> = ({ fu, dato, onCopiar }) => {
	const a = fu.anuncio;
	return (
		<div className="crm-tarjeta fz2-info fz-fuente">
			<div className="fx-t-cab"><h2>Fuente</h2>{fu.clic ? <span className="fz-fu-tag">Clic a anuncio</span> : null}</div>
			<div className="fz2-datos">
				{dato("Llegó por", fu.fuente)}
				{dato("Llegó el", fu.primerContacto ? fechaLarga(fu.primerContacto) : null)}
				{fu.canal ? dato("Escribió al", <span title={fu.canalTel || undefined}>{fu.canal}</span>) : null}
				{a?.nombre || a?.id ? dato("Anuncio", <span className="fz-fu-ad"><span title={a.titular || a.nombre || undefined}>{a.nombre || a.titular || "Sin nombre"}</span>
					{a.url ? <a href={a.url} target="_blank" rel="noopener noreferrer" className="fx-link">Ver</a> : null}</span>) : null}
				{a?.campana ? dato("Campaña", <span title={a.conjunto ? "Conjunto: " + a.conjunto : undefined}>{a.campana}</span>) : null}
				{a?.id ? dato("ID anuncio", <button type="button" className="fz-fu-id" onClick={() => onCopiar(a.id!)} title="Copiar">{a.id}</button>) : null}
				{fu.detalle ? dato("Detalle", fu.detalle) : null}
				{fu.ultimo ? dato("Volvió por", <span title={fu.ultimo.campana || undefined}>{fu.ultimo.anuncio || fu.ultimo.fuente || "Anuncio"} · {fechaLarga(fu.ultimo.at).split(",")[0]}</span>) : null}
			</div>
		</div>
	);
};

interface Props {
	campusId: number;
	prospectId: number;
	catalogs: ProspectCatalogs | null;
	volverTexto: string;
	onBack: () => void;
	onOpen: (id: number) => void;
	/** La lista vuelve a pedir su página (status, próxima actividad…). */
	onChanged: () => void;
	/** Solo si ve WappChat (Esteban y Supervisión, por ahora). */
	onWappchat?: (id: number) => void;
	/** Anterior/siguiente de la lista de la que vino (como Zoho). */
	nav?: FichaNav;
}

export interface FichaNav { pos: number; total: number; prev: (() => void) | null; next: (() => void) | null }

const Flechas: React.FC<{ nav: FichaNav }> = ({ nav }) => (
	<span className="fz2-nav">
		<button type="button" disabled={!nav.prev} onClick={() => nav.prev?.()} aria-label="Cliente anterior" title="Cliente anterior de la lista">
			<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg></button>
		<span className="pos">{nav.pos.toLocaleString("es-MX")} de {nav.total.toLocaleString("es-MX")}</span>
		<button type="button" disabled={!nav.next} onClick={() => nav.next?.()} aria-label="Cliente siguiente" title="Cliente siguiente de la lista">
			<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6" /></svg></button>
	</span>
);

const SIGUIENTE: { key: string; label: string; tipo: string | null }[] = [
	{ key: "", label: "Ninguno", tipo: null }, { key: "llamar", label: "Volver a llamar", tipo: "llamar" }, { key: "cita", label: "Agendar cita", tipo: "cita" },
	{ key: "info", label: "Mandar información", tipo: "info" },
];

/** WhatsApp (historial): solo lectura, como "Woztell logs" en Zoho. Lo más nuevo abajo. */
const HistorialWa: React.FC<{ campusId: number; prospectId: number; refresco: number }> = ({ campusId, prospectId, refresco }) => {
	const http = HttpManager.getInstance();
	const [events, setEvents] = useState<ProspectEvent[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [page, setPage] = useState<number>(1);
	const [cargando, setCargando] = useState<boolean>(false);
	const cargar = useCallback(async (p: number) => {
		setCargando(true);
		try {
			const r = await http.prospectsService.events(campusId, prospectId, { kind: ["whatsapp"] }, 60, p);
			const lote = [...r.data.data].reverse();
			setEvents((prev) => (p === 1 ? lote : [...lote.filter((e) => !prev.some((x) => x.id === e.id)), ...prev]));
			setTotal(r.data.total); setPage(p);
		} catch (e) { /* el aviso de arriba ya dice que es solo historial */ }
		finally { setCargando(false); }
	}, [campusId, prospectId]);
	useEffect(() => { setEvents([]); cargar(1); }, [cargar, refresco]);
	return <Conversacion campusId={campusId} prospectId={prospectId} events={events} hayMas={events.length < total} onMas={() => cargar(page + 1)} cargando={cargando} />;
};

/** Registrar llamada: cómo salió una llamada (las de WAPP ya quedan solas en la bitácora; esto es para anotar el resultado o una llamada hecha por fuera). */
const FormLlamada: React.FC<{ campusId: number; prospectId: number; inicial?: string; onGuardada: () => void; onCancelar: () => void; onCita: () => void }> = ({ campusId, prospectId, inicial, onGuardada, onCancelar, onCita }) => {
	const http = HttpManager.getInstance();
	const [result, setResult] = useState<string>(inicial || "contesto");
	const [nota, setNota] = useState<string>("");
	const [sig, setSig] = useState<string>("");
	const [fecha, setFecha] = useState<string>(new Date(Date.now() - 6 * 3600000 + 86400000).toISOString().slice(0, 10));
	const [horaV, setHoraV] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const guardar = async () => {
		setBusy(true); setError(null);
		try {
			await http.prospectsService.call(campusId, prospectId, { result, note: nota.trim() || undefined, source: "manual" });
			const s = SIGUIENTE.find((x) => x.key === sig);
			// La cita se agenda con su formulario de tres pasos (plantel o digital, día, hora) en cuanto se guarda la llamada.
			if (s?.tipo === "cita") { onGuardada(); onCita(); return; }
			if (s?.tipo && fecha) {
				// Sin hora va solo el día (nunca 23:59 inventado).
				const titulo = nota.trim() ? nota.trim().slice(0, 140) : null;
				await http.prospectsService.crearActividad(campusId, prospectId, horaV ? { tipo: s.tipo, titulo, at: localMxAIso(fecha + "T" + horaV), hora_exacta: true }
					: { tipo: s.tipo, titulo, fecha, hora_exacta: false });
			}
			onGuardada();
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};
	return (
		<div className="crm-tarjeta crm-form-llam">
			<b>Registrar llamada</b> <span className="crm-apagado" style={{ fontSize: 13 }}>(cómo salió la llamada)</span>
			<div className="crm-tipos llam" role="group" aria-label="Cómo salió la llamada">
				{RESULTADOS_LLAMADA.map((r) => <button key={r.key} className={"crm-tipo" + (result === r.key ? " on" : "")} aria-pressed={result === r.key} onClick={() => setResult(r.key)}>{r.label}</button>)}
			</div>
			<div className="crm-form">
				<div className="ancho"><label htmlFor="ll-nota">Qué se habló</label><textarea id="ll-nota" rows={2} value={nota} onChange={(e) => setNota(e.target.value)} /></div>
				<div><label htmlFor="ll-sig">Siguiente paso</label>
					<select id="ll-sig" value={sig} onChange={(e) => setSig(e.target.value)}>{SIGUIENTE.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</select></div>
				{sig && sig !== "cita" ? <div><label htmlFor="ll-f">Cuándo</label><input id="ll-f" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} /></div> : null}
				{sig && sig !== "cita" ? <div><label htmlFor="ll-h">Hora <small>(opcional)</small></label><input id="ll-h" type="time" value={horaV} onChange={(e) => setHoraV(e.target.value)} /></div> : null}
			</div>
			{result === "equivocado" ? <div className="crm-pie">Su status pasa a Número equivocado.</div> : null}
			{error ? <div className="crm-aviso error" style={{ marginTop: 10 }}>{error}</div> : null}
			<div className="crm-botones">
				<button className="crm-b naranja" disabled={busy} onClick={guardar}>{busy ? "Guardando…" : "Guardar llamada"}</button>
				<button className="crm-b" onClick={onCancelar}>Cancelar</button>
			</div>
		</div>
	);
};

/**
 * La ficha del CRM como en Zoho (prototipo aprobado 2026-10-02): arriba quién es, status y los botones de siempre;
 * a la izquierda las secciones (Visión general, Notas, Actividades, Llamadas, WhatsApp, Cronología); a la derecha
 * próximas actividades, cotización y horarios para copiar a Woztell.
 */
const CrmFicha: React.FC<Props> = ({ campusId, prospectId, catalogs, volverTexto, onBack, onOpen, onChanged, onWappchat, nav }) => {
	const http = HttpManager.getInstance();
	const navigate = useNavigate();
	const [det, setDet] = useState<ProspectDetails | null>(null);
	const acceso = useAcceso(det);
	const [acts, setActs] = useState<Actividades | null>(null);
	const [sec, setSec] = useState<Sec>("general");
	const [refresco, setRefresco] = useState<number>(0);
	const [abrirAct, setAbrirAct] = useState<number>(0);
	const [llamada, setLlamada] = useState<boolean>(false);
	const [llamadaInicial, setLlamadaInicial] = useState<string>("contesto");
	const [nota, setNota] = useState<string>("");
	const [notaAbierta, setNotaAbierta] = useState<boolean>(false);
	const [notaTitulo, setNotaTitulo] = useState<string>("");
	const [todasNotas, setTodasNotas] = useState<boolean>(false);
	const [notaError, setNotaError] = useState<string | null>(null);
	const [pideMotivo, setPideMotivo] = useState<number | null>(null);
	const [motivo, setMotivo] = useState<string>("");
	const [modal, setModal] = useState<"editar" | "promo" | "reasignar" | null>(null);
	const [reasignarA, setReasignarA] = useState<string>("");
	const [reasignarPlantel, setReasignarPlantel] = useState<number | null>(null);
	const [mas, setMas] = useState<boolean>(false);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	// Promesa con fecha (Esteban, 2026-10-13: "sin fecha no hay Promesa"): el status Promesa abre la cita ahí mismo.
	const [cita, setCita] = useState<{ promesa: boolean } | null>(null);
	const vivo = useRef<number>(prospectId);

	const cargarDetalle = useCallback(async () => {
		try {
			const r = await http.prospectsService.details(campusId, prospectId);
			if (vivo.current === prospectId) setDet(r.data);
		} catch (e) { setError(errorTexto(e)); }
	}, [campusId, prospectId]);
	const cargarActs = useCallback(async () => {
		try {
			const r = await http.prospectsService.actividades(campusId, prospectId);
			if (vivo.current === prospectId) setActs(r.data);
		} catch (e) { setError(errorTexto(e)); }
	}, [campusId, prospectId]);

	useEffect(() => {
		vivo.current = prospectId;
		setDet(null); setActs(null); setSec("general"); setLlamada(false); setCita(null); setNota(""); setNotaTitulo(""); setNotaAbierta(false); setNotaError(null); setPideMotivo(null); setModal(null); setError(null); setAviso(null);
		cargarDetalle(); cargarActs();
	}, [prospectId]);
	useEffect(() => {
		if (!mas) return;
		const cerrar = (e: MouseEvent) => { if (!(e.target as HTMLElement).closest(".crm-menu")) setMas(false); };
		document.addEventListener("mousedown", cerrar);
		return () => document.removeEventListener("mousedown", cerrar);
	}, [mas]);

	/** Después de guardar algo: ficha, actividades, bitácora y la lista de atrás al día. */
	const todoAlDia = () => { cargarDetalle(); cargarActs(); setRefresco((n) => n + 1); onChanged(); marcador.refrescar(); };
	const accion = async (fn: () => Promise<any>) => {
		setBusy(true); setError(null);
		try { await fn(); todoAlDia(); }
		catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};
	// La ficha completa se desplaza (encabezado incluido); al cambiar de pestaña se queda en las pestañas.
	const ir = (s: Sec) => {
		setSec(s);
		const f = document.querySelector(".crm-ficha.fx") as HTMLElement | null, t = document.querySelector(".fx-tabs") as HTMLElement | null;
		if (f && t && f.scrollTop > t.offsetTop) f.scrollTo({ top: t.offsetTop });
	};
	const copiar = (t: string, msg = "Copiado. Pégalo en el chat de Woztell.") => {
		navigator.clipboard?.writeText(t).then(() => { setAviso(msg); window.setTimeout(() => setAviso(null), 2500); },
			() => setError("No se pudo copiar: selecciona el texto a mano."));
	};
	const ponerStatus = (id: number, conMotivo: string | null = null) => {
		const s = catalogs?.statuses.find((x) => x.id === id);
		if (!s || !s.bool_manual) return;
		if (s.bool_needs_reason && !conMotivo) { setPideMotivo(id); setMotivo(""); return; }
		if (id === det?.prospect.status_id && (!s.bool_needs_reason || conMotivo === det?.prospect.potencial_motivo)) return;
		setPideMotivo(null);
		if (id === 4) { setCita({ promesa: true }); return; }
		accion(() => http.prospectsService.status(campusId, prospectId, id, s.bool_needs_reason ? conMotivo : null));
	};
	/** "Al Marcador ↑": hasta arriba de mi lista de llamadas de hoy. */
	const alMarcador = () => http.prospectsService.dialerPin(campusId, prospectId)
		.then(() => { marcador.refrescar(); setAviso("Listo: va hasta arriba de tu marcador de hoy."); window.setTimeout(() => setAviso(null), 3500); })
		.catch((e) => setError(errorTexto(e)));
	const guardarNota = async () => {
		if (!nota.trim()) return;
		setBusy(true); setNotaError(null);
		try {
			await http.prospectsService.note(campusId, prospectId, nota.trim(), "nota", notaTitulo.trim());
			setNota(""); setNotaTitulo(""); setNotaAbierta(false); todoAlDia();
		} catch (e) { setNotaError(errorTexto(e)); }
		finally { setBusy(false); }
	};
	const guardarCursos = (v: string) => {
		setDet((d) => (d ? { ...d, prospect: { ...d.prospect, course_interest_text: v } } : d));
		http.prospectsService.update(campusId, prospectId, { courseInterest: v }).then(() => onChanged()).catch((e) => setError(errorTexto(e)));
	};

	// Al colgar una llamada hecha con WAPP a esta persona (antes del return: es un hook). Si no contestaron se registra sola
	// ("No contestó" / "Ocupado"); si contestaron se abre "Registrar llamada" para dejar qué se habló.
	const alDia = useRef<(() => void) | null>(null);
	// Guardó la tarjeta de cierre del marcador para esta persona: actividades y siguiente acción al día.
	useEffect(() => {
		const f = (e: Event) => { if ((e as CustomEvent).detail === prospectId) alDia.current?.(); };
		window.addEventListener("wapp:ficha-al-dia", f);
		return () => window.removeEventListener("wapp:ficha-al-dia", f);
	}, [prospectId]);
	useEffect(() => telefono.alTerminar((f) => {
		if (f.prospectId !== prospectId) return;
		// La llamó el auto-marcador: la tarjeta de cierre del panel la registra (no dos veces).
		if (marcador.atiende(f.prospectId)) { window.setTimeout(() => alDia.current?.(), 1500); return; }
		const sola = resultadoSinRespuesta(f);
		if (!sola) { setSec("general"); setLlamadaInicial(resultadoInicial(f)); setLlamada(true); return; }
		HttpManager.getInstance().prospectsService.call(campusId, prospectId, { result: sola, source: "manual", durationSec: f.durationSec, callId: f.callId })
			.then(() => { setAviso(sola === "ocupado" ? "Llamada registrada: ocupado." : "Llamada registrada: no contestó."); window.setTimeout(() => setAviso(null), 4000); alDia.current?.(); })
			.catch(() => { setSec("general"); setLlamada(true); });
	}), [prospectId, campusId]);
	if (!det) return <div className="crm-ficha"><div className="ventas-vacio">{error || "Cargando…"}</div></div>;
	const p = det.prospect;
	const cuenta = (k: string) => Number(det.eventCounts.find((c) => c.kind === k)?.n || 0);
	const vencidas = (acts?.abiertas || []).filter((a) => venceTexto(a).tono === "vencido").length;
	const n = (v: number) => v.toLocaleString("es-MX");
	const planteles = det.campuses.map((c) => c.name.replace("Washington Academy ", "")).join(", ") || plantelCorto(catalogs, p.campus_id);
	const fu = det.fuente || null;
	// Como Zoho: Visión general, Actividades y Cronología (las notas van en el resumen; los mensajes, en el chat).
	const SECS: { key: Sec; label: string; n?: string; oro?: boolean }[] = [
		{ key: "general", label: "Resumen" },
		{ key: "actividades", label: "Actividades", n: acts ? String(acts.abiertas.length) : "…", oro: vencidas > 0 },
		{ key: "cronologia", label: "Cronología" },
	];
	const inscribir = p.etapa_id === 1 ? () => navigate("/enrollmentPrerequests", { state: { nuevaSolicitud: { phone: p.phone_e164, name: nombreReal(p) || "", plantel: p.campus_id || null, curso: String(p.course_interest_text || "").split(/[,;\n]/)[0].trim() } } })
		: p.etapa_id === 3 || p.etapa_id === 4 ? () => navigate("/enrollmentRequests", { state: { phone: p.phone_e164, name: nombreReal(p) || "", reingreso: true } }) : null;
	const agendar = () => { ir("actividades"); setAbrirAct((x) => x + 1); };
	const registrarLlamada = () => { ir("general"); setLlamadaInicial("contesto"); setLlamada(true); };
	alDia.current = todoAlDia;
	// "Llamar" marca con el teléfono de WAPP; la llamada queda sola en la bitácora. El teléfono ya no se apaga: si se está
	// reconectando, se reconecta y marca en cuanto queda listo (Esteban, 2026-10-12).
	const llamar = async () => {
		if (!p.phone_e164) return;
		if (telefono.ocupado) { setAviso("Ya estás en una llamada."); window.setTimeout(() => setAviso(null), 6000); return; }
		if (!telefono.listo) setAviso("Conectando el teléfono…");
		const id = await telefono.llamarYa(aMarcable(p.phone_e164), { prospectId, name: nombreReal(p) || null });
		if (id) { setAviso(null); return; }
		setAviso("El teléfono de WAPP no logró conectarse. Se sigue reconectando solo: vuelve a dar Llamar en unos segundos.");
		window.setTimeout(() => setAviso(null), 6000);
	};

	const act = det.wapp?.active || null;
	const prox = (acts?.abiertas || [])[0] || null;
	const av = acceso;
	const flag = prox ? banderin(prox.due_dia || prox.due_at) : null;
	const real = nombreReal(p);
	const wa = (p as any).wa_nombre || det.waNombre || null;
	const titulo = real || (pareceNombre(wa) ? limpiarWa(wa) : nombreWa(p.phone_e164));
	const terr = territorio(p);
	const MOVIMIENTOS = ["nota", "llamada", "tarea", "cambio", "asignacion", "sistema", "promo"];
	// Lo que se sabía en Zoho va en Información como un dato más (2026-10-19); quién la llevaba en Zoho, en Cronología.
	const correoZoho = !p.email ? (det.datosCliente || []).find(([t]) => t === "Correo")?.[1] || null : null;
	const extras = (det.datosCliente || []).filter(([t]) => !(correoZoho && t === "Correo"));
	const dato = (t: string, v: React.ReactNode, ancho = false) => (
		<div className={"fz-dato" + (ancho ? " ancho" : "")}><span className="fz-l">{t}</span><span className="fz-v">{v === null || v === undefined || v === "" ? <span className="fx-vacio">—</span> : v}</span></div>
	);

	return (
		<div className={"crm-ficha fx fz fz2" + (av.soloVer ? " solo-ver" : "")}>
			<div className="fx-cab fz2-cab">
				<div className="fz2-fila">
					<button className="fx-volver fz2-volver" onClick={onBack} aria-label={"Volver a la cartera (" + volverTexto + ")"} title={"Volver a " + volverTexto}>
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
						<span className="txt">Cartera</span></button>
					{nav ? <Flechas nav={nav} /> : null}
					<div className="fx-avatar" aria-hidden="true">{inicialesDe({ ...p, wa_nombre: wa })}</div>
					<div className="fz2-quien">
						<div className="fz2-nombre-l">
							<h1 className={"fx-nombre" + (real ? "" : " sin")}>{titulo}{!real && pareceNombre(wa) ? <LogoWa size={18} /> : null}</h1>
							{!real && !av.soloVer ? <button className="fz-poner" onClick={() => setModal("editar")}>Poner su nombre</button> : null}
						</div>
						<div className="fz2-sub">
							<span>{p.phone_e164 ? telefonoBonito(p.phone_e164) : "sin teléfono"}</span>
							{terr ? <span>{terr}</span> : null}
							{wa && limpiarWa(wa) !== titulo && waDiceAlgo(wa) ? <span className="fz-wa"><LogoWa size={14} /> {wa}</span> : null}
						</div>
						<EquipoChips equipo={det.equipo} venta={p.owner_zoho_name} ventaMia={!!p.owner_zoho_id && p.owner_zoho_id === catalogs?.myZohoId} />
					</div>
					<div className="fz2-acciones solo-pc">
						{onWappchat ? <button className="crm-b fz2-wapp" onClick={() => onWappchat(prospectId)}><LogoWa size={17} label="WappChat" />WappChat</button> : null}
						<button className="crm-b naranja" disabled={!p.phone_e164} onClick={llamar}>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" /></svg>Llamada</button>
						{av.puedeNota ? <button className="crm-b azul" onClick={() => { ir("general"); window.setTimeout(() => (document.querySelector(".fz2-notas textarea") as HTMLTextAreaElement | null)?.focus(), 0); }}>+ Nota</button> : null}
						{av.soloVer ? null : <>
						<button className="crm-b morado" onClick={agendar}>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18" /></svg>Agendar</button>
						{inscribir ? <button className="crm-b negro" onClick={inscribir}>{p.etapa_id === 1 ? "Inscribir" : "Reinscribir"}</button> : null}
						<button className="crm-b" disabled={!p.phone_e164} onClick={alMarcador} title="Lo pone hasta arriba de tu lista de hoy">Al Marcador ↑</button>
						{(catalogs?.puedeReasignar ?? catalogs?.isSupervisor) ? <button className="crm-b" onClick={() => setModal("reasignar")}>Reasignar</button> : null}
						<div className="crm-menu">
							<button className="crm-b" aria-haspopup="menu" aria-expanded={mas} aria-label="Más acciones" onClick={() => setMas(!mas)}>⋯</button>
							{mas ? (
								<div className="crm-menu-lista" role="menu" onClick={() => setMas(false)}>
									<button role="menuitem" onClick={() => setModal("editar")}>Editar datos</button>
									{det.student ? <button role="menuitem" onClick={() => navigate(`/students?alumno=${det.student!.id}&plantel=${det.wapp?.active?.campus_id || campusId}`)}>Ficha de alumna</button> : null}
								</div>
							) : null}
						</div>
						</>}
					</div>
				</div>
				{/* Teléfono (Esteban, 2026-10-12): Reasignar y el menú también aquí, no solo en WappChat; y las flechas. */}
				<div className="fz2-movil">
					{av.soloVer ? null : <>
					<button className="crm-b" disabled={!p.phone_e164} onClick={alMarcador} title="Lo pone hasta arriba de tu lista de hoy">Al Marcador ↑</button>
					{(catalogs?.puedeReasignar ?? catalogs?.isSupervisor) ? <button className="crm-b" onClick={() => setModal("reasignar")}>Reasignar</button> : null}
					<div className="crm-menu">
						<button className="crm-b" aria-haspopup="menu" aria-expanded={mas} aria-label="Más acciones" onClick={() => setMas(!mas)}>⋯</button>
						{mas ? (
							<div className="crm-menu-lista" role="menu" onClick={() => setMas(false)}>
								<button role="menuitem" onClick={() => setModal("editar")}>Editar datos</button>
								{det.student ? <button role="menuitem" onClick={() => navigate(`/students?alumno=${det.student!.id}&plantel=${det.wapp?.active?.campus_id || campusId}`)}>Ficha de alumna</button> : null}
							</div>
						) : null}
					</div>
					</>}
					{nav ? <Flechas nav={nav} /> : null}
				</div>
				<div className="fz2-campos">
					<div className="fz2-campo"><span className="fx-lbl">Status</span>
						{av.soloVer ? <span className="fz-solo-txt">{det.status}{p.potencial_motivo ? " · " + p.potencial_motivo : ""}</span>
							: <StatusMenu statuses={catalogs?.statuses || []} actual={p.status_id} motivo={p.potencial_motivo || null} busy={busy} onElegir={ponerStatus}
							motivos={(catalogs?.motivosPotencial || []).filter((m) => !MOTIVOS_OCULTOS.includes(m))} />}</div>
					<div className="fz2-campo"><span className="fx-lbl">Etapa</span><PildoraEtapa etapa={p.etapa_id || null} /></div>
					<div className="fz2-campo fz2-interes"><span className="fx-lbl">{act ? "Estudia" : "Le interesa"}</span>
						{act ? <span className="fz-estudia">{cursoCorto(act.curso)}{act.modulo && act.modulo !== act.curso ? " · " + act.modulo : ""}<small>{act.horario}</small></span>
							: av.soloVer ? <span className="fz-solo-txt">{p.course_interest_text || "—"}</span>
							: <CursosCompacto value={p.course_interest_text || ""} onChange={guardarCursos} />}</div>
					{pideMotivo && catalogs ? (
						<div className="fz2-campo"><span className="fx-lbl">¿Por qué Potencial?</span>
							<span className="crm-motivo">
								<select value={motivo} onChange={(e) => setMotivo(e.target.value)} aria-label="Motivo" autoFocus>
									<option value="">Motivo…</option>
									{catalogs.motivosPotencial.filter((m) => !["heredado de Zoho", "sin status en Zoho"].includes(m)).map((m) => <option key={m} value={m}>{m}</option>)}
								</select>
								<button className="crm-b chico naranja" disabled={busy || !motivo}
									onClick={() => accion(() => http.prospectsService.status(campusId, prospectId, pideMotivo, motivo)).then(() => setPideMotivo(null))}>Guardar</button>
								<button className="crm-b chico" onClick={() => setPideMotivo(null)}>Cancelar</button>
							</span>
						</div>
					) : null}
				</div>
			</div>

			<nav className="fx-tabs" aria-label="Secciones de la ficha">
				{SECS.map((s) => (
					<button key={s.key} className={"fx-tab" + (sec === s.key ? " on" : "")} aria-current={sec === s.key ? "true" : undefined} onClick={() => ir(s.key)}>
						{s.label}{s.n !== undefined ? <span className={"fx-num" + (s.oro ? " oro" : "")}>{s.n}</span> : null}
					</button>
				))}
			</nav>

			<div className="fx-cuerpo fz2-cuerpo">
				<select className="crm-movil" value={sec} onChange={(e) => ir(e.target.value as Sec)} aria-label="Sección">
					{SECS.map((s) => <option key={s.key} value={s.key}>{s.label}{s.n ? " · " + s.n : ""}</option>)}
				</select>
				<AvisoSoloVer acceso={av} className="fz2-ancho" />
				{error ? <div className="crm-aviso error fz2-ancho">{error}</div> : null}
				{aviso ? <div className="crm-aviso ok fz2-ancho">{aviso}</div> : null}
				{llamada ? <div className="fz2-ancho"><FormLlamada key={llamadaInicial} campusId={campusId} prospectId={prospectId} inicial={llamadaInicial} onGuardada={() => { setLlamada(false); todoAlDia(); }} onCancelar={() => setLlamada(false)}
					onCita={() => setCita({ promesa: p.status_id !== 4 })} /></div> : null}

				{sec === "general" ? (
					<>
						{/* Izquierda: la ficha (qué sigue, sus datos, sus movimientos). Igual de visible que las notas (Esteban, 2026-10-06). */}
						<div className="fz2-colficha">
							<div className="fz-sig">
								<div className="fz-sig-cab"><span className="fx-lbl">Siguiente acción</span>{acts && acts.abiertas.length > 1 ? <button className="fx-link" onClick={() => ir("actividades")}>{acts.abiertas.length} abiertas</button> : null}</div>
								{prox ? (
									<div className="fz-sig-fila">
										{flag ? <span className={"cl-flag " + flag.tono}>{flag.t}</span> : null}
										<span className="fz-sig-tx"><b>{prox.tipoLabel}</b>{prox.titulo ? <span className="fz-sig-desc">{prox.titulo}</span> : null}<small>{prox.owner_name || ""}</small></span>
										<button className="crm-b chico" onClick={() => ir("actividades")}>Completar</button>
									</div>
								) : (
									<div className="fz-sig-fila"><span className="fz-sig-tx fx-vacio">Nada agendado</span><button className="crm-b chico morado" onClick={agendar}>+ Agendar</button></div>
								)}
							</div>
							{/* Equipo del cliente arriba de Información (Esteban, 2026-10-13: "debería aparecer más arriba"). */}
							<div className="cv-lado"><EquipoCliente campusId={campusId} det={det} catalogs={catalogs} onAccion={accion} /></div>
							<div className="crm-tarjeta fz2-info">
								<div className="fx-t-cab"><h2>Información</h2><button className="crm-b chico" onClick={() => setModal("editar")}>Editar</button></div>
								<div className="fz2-datos">
									{dato("Nombre", real || <span className="fx-vacio">Sin nombre</span>, true)}
									{dato("Móvil", p.phone_e164 ? telefonoBonito(p.phone_e164) : null)}
									{p.phone_alt ? dato("Otro teléfono", telefonoBonito(p.phone_alt)) : null}
									{dato("Correo", p.email || correoZoho, true)}
									{dato("Dueña", p.owner_zoho_name || <span className="fx-vacio">Sin asignar</span>)}
									{dato("Equipo", p.team_code)}
									{dato("Plantel", planteles)}
									{dato("Creado", p.first_contact_at ? <>{fechaLarga(p.first_contact_at)}{(p as any).recontacto_at ? <span className="fz2-rec" title={"Recontactó el " + fechaLarga((p as any).recontacto_at)}> · ↻ {fechaLarga((p as any).recontacto_at).split(",")[0]}</span> : null}</> : null)}
									{dato("Últ. contacto", cuando(p.last_activity_at))}
									{det.student ? dato("Alumna", det.student.identity_code) : null}
									{extras.map(([t, v], i) => <React.Fragment key={i}>{dato(t, v, v.length > 60)}</React.Fragment>)}
								</div>
								{det.zohoAdjuntos && det.zohoAdjuntos.length ? (
									<div className="fz-zoho-adj">
										<div className="fz-zoho-sub">Archivos ({det.zohoAdjuntos.length})</div>
										{det.zohoAdjuntos.map((a) => (
											<button key={a.zoho_id} type="button" className="fz-zoho-arch" title={a.file_name}
												onClick={() => abrirArchivo({ origen: "CRM", nombre: a.file_name, cargar: () => http.prospectsService.zohoAdjunto(campusId, prospectId, a.zoho_id).then((r) => ({ blob: r.data, nombre: a.file_name })) })
													.catch((e) => setError(errorTexto(e)))}>
												<span className="fz-zoho-nom">{a.file_name}</span>
												<small>{a.zoho_created_at ? fechaLarga(a.zoho_created_at).split(",")[0] : ""}</small>
											</button>
										))}
									</div>
								) : null}
							</div>
							{fu ? <FuenteCard fu={fu} dato={dato} onCopiar={(t) => copiar(t, "ID del anuncio copiado.")} /> : null}
							<LadoCliente campusId={campusId} det={det} onEditar={() => setModal("editar")} onEnviarAlChat={copiar} soloFicha sinEquipo
								onPaso={(paso) => { if (paso === "promesa") { setCita({ promesa: p.status_id !== 4 }); return; } ir("actividades"); setAbrirAct((x) => x + 1); }}
								onAbrir={onOpen} onQuitarVinculo={(linkId) => accion(() => http.prospectsService.removeLink(campusId, prospectId, linkId))}
								onPromo={() => setModal("promo")} onAgendar={() => { ir("actividades"); setAbrirAct((x) => x + 1); }}
								catalogs={catalogs} onAccion={accion} />
							<div className="crm-tarjeta fx-act">
								<div className="fx-t-cab"><h2>Últimos movimientos</h2><button className="fx-link" onClick={() => ir("cronologia")}>Ver todo</button></div>
								<CrmBitacora campusId={campusId} prospectId={prospectId} kinds={MOVIMIENTOS.filter((k) => k !== "nota")} refresco={refresco} limite={5} vacio="Todavía no hay movimientos." />
							</div>
						</div>
						{/* Derecha: las notas, mismo ancho que la ficha. */}
						<div className="fz2-colnotas">
							<div className="crm-tarjeta fx-notas fz2-notas">
								<div className="fx-t-cab"><h2>Notas <span className="fx-num">{n(cuenta("nota"))}</span></h2>{cuenta("nota") > 5 ? <button className="fx-link" onClick={() => setTodasNotas(!todasNotas)}>{todasNotas ? "Ver menos" : "Ver todas"}</button> : null}</div>
								{av.puedeNota ? <div className="fz2-nueva">
									<div className="fz2-nueva-campos">
										<input value={notaTitulo} onChange={(e) => setNotaTitulo(e.target.value)} maxLength={120} placeholder="Título (opcional)" aria-label="Título de la nota" />
										<textarea rows={2} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Escribe lo que platicaron…" aria-label="Nueva nota"
											onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) guardarNota(); }} />
									</div>
									<button className="crm-b azul chico" disabled={busy || !nota.trim()} onClick={guardarNota}>{busy ? "Guardando…" : "Guardar"}</button>
								</div> : null}
								{notaError && !notaAbierta ? <div className="crm-aviso error">{notaError}</div> : null}
								<CrmBitacora campusId={campusId} prospectId={prospectId} kinds={["nota"]} refresco={refresco} limite={todasNotas ? undefined : 5} vacio="Todavía no tiene notas." />
							</div>
						</div>
					</>
				) : null}

				{sec === "actividades" ? (
					<div className="fz2-ancho">
						<CrmActividades campusId={campusId} prospectId={prospectId} data={acts} abrirForm={abrirAct} onCambio={todoAlDia} plantelId={p.campus_id || campusId} />
					</div>
				) : null}

				{sec === "cronologia" ? (
					<div className="fz2-ancho">
						<p className="fz-nota">Todo lo que ha pasado con este contacto: quién hizo qué y cuándo. Los mensajes están en WappChat.</p>
						<CrmBitacora campusId={campusId} prospectId={prospectId} kinds={MOVIMIENTOS} refresco={refresco} vacio="Todavía no hay movimientos." cronologia />
					</div>
				) : null}
			</div>

			{/* Teléfono: las acciones de siempre, grandes y fijas abajo (Esteban, 2026-10-03). */}
			<nav className="crm-barra-movil" aria-label="Acciones">
				{p.phone_e164 ? <a className="crm-bm naranja" href={"tel:" + p.phone_e164} onClick={registrarLlamada}>Llamar</a>
					: <button className="crm-bm naranja" disabled>Llamar</button>}
				<button className="crm-bm azul" onClick={() => (onWappchat ? onWappchat(prospectId) : null)}>WappChat</button>
				{av.puedeNota ? <button className="crm-bm azul" onClick={() => setNotaAbierta(true)}>+ Nota</button> : null}
				{av.soloVer ? null : <button className="crm-bm morado" onClick={agendar}>Agendar</button>}
				{inscribir && !av.soloVer ? <button className="crm-bm negro" onClick={inscribir}>{p.etapa_id === 1 ? "Inscribir" : "Reinscribir"}</button> : null}
			</nav>
			{cita ? <ModalCita campusId={campusId} prospectId={prospectId} plantelId={p.campus_id || campusId} modo="cita" promesa={cita.promesa}
				aviso={cita.promesa ? "Una Promesa lleva fecha: agenda la cita o el seguimiento." : null}
				onGuardado={() => { setCita(null); todoAlDia(); }} onCancelar={() => setCita(null)} /> : null}
			{notaAbierta ? <NotaModal texto={nota} setTexto={setNota} titulo={notaTitulo} setTitulo={setNotaTitulo} busy={busy} error={notaError} onGuardar={guardarNota}
				onClose={() => { setNotaAbierta(false); setNotaError(null); }} /> : null}
			{modal === "editar" ? <EditarDatos campusId={campusId} det={det} catalogs={catalogs} onClose={() => setModal(null)}
				onGuardado={(d) => { setDet(d); setModal(null); setRefresco((x) => x + 1); onChanged(); }} /> : null}
			{modal === "promo" ? <PromoModal campusId={campusId} det={det} asesora={p.owner_zoho_name || ""} onClose={() => setModal(null)}
				onAlChat={(t) => { copiar(t); setModal(null); }} wa={null} onEnviado={todoAlDia} /> : null}
			{modal === "reasignar" && catalogs ? (
				<div className="crm-tarjeta crm-reasignar-pop" role="dialog" aria-label="Reasignar" style={{ position: "fixed", top: 120, zIndex: 30, width: 340, boxShadow: "0 8px 24px rgba(0,0,0,.18)" }}>
					<h4>Reasignar a</h4>
					<div className="crm-form">
						<div className="ancho">
							<ReasignarDestino campusId={campusId} asesoras={catalogs.advisors} value={{ advisorId: reasignarA, plantel: reasignarPlantel }}
								onChange={(v) => { setReasignarA(v.advisorId); setReasignarPlantel(v.plantel); }} />
						</div>
					</div>
					<div className="crm-botones">
						<button className="crm-b negro" disabled={busy || !reasignarA} onClick={() => accion(() => http.prospectsService.assign(campusId, prospectId, reasignarA, null, reasignarPlantel)).then(() => { setModal(null); setReasignarPlantel(null); })}>Reasignar</button>
						<button className="crm-b" onClick={() => setModal(null)}>Cancelar</button>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default CrmFicha;
